import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectBrandMenuConstraints,
  selectBrandMenuItemsByName,
  selectTopLevelMenuItems,
} from '../selectors/menu';
import { useReminder } from '../contexts/ReminderContext';
import { TTS_PROMPT, TTS_PROMPT_PREFIX } from '../constants';
import { SLASH_COMMAND_REMINDERS } from '../utils/constants';
import { SlashCommand } from '../types/menu';
import { selectValidCartItems } from '../selectors/cart';
import { getAllCartItemNames } from '../utils/cart';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { IConstraint } from '../types/brandMenu';
import { evaluateItemNotOnCheckConstraint } from '../utils/menuUnification.utils';
import { skipUpsellInQueue } from '../reducers/dialogSlice';
import { selectDialog } from '../selectors/dialog';
import { selectOrderValues } from '../selectors/order';

const isUpsellCommand = (cmd: SlashCommand) =>
  cmd.intent.startsWith(SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX);

interface ShowReminderOptions {
  /**
   * When true, filter `upsell_end_of_order` out of both the displayed reminder
   * text and the queue seeded into redux. Used by readback so the upsell prompt
   * doesn't auto-populate or re-populate when the agent skips/repeats Confirm.
   * See HITL-1253.
   */
  excludeUpsell?: boolean;
}

interface SlashCommandReminderResult {
  hasRequiredCommands: boolean;
  availableSlashCommands: SlashCommand[];
  showSlashCommandReminder: (options?: ShowReminderOptions) => void;
}

/**
 * Custom hook that provides slash command reminder functionality.
 * Detects available slash commands from TTS prompt items and provides
 * a function to show the appropriate reminder message.
 */
export const useSlashCommandReminder = (): SlashCommandReminderResult => {
  const topLevelMenuItems = useAppSelector(selectTopLevelMenuItems) || {};
  const brandMenuConstraints = useAppSelector(selectBrandMenuConstraints) || {};
  const brandMenuItemsByName = useAppSelector(selectBrandMenuItemsByName) || {};
  const validCartItems = useAppSelector(selectValidCartItems);
  const dialogState = useAppSelector(selectDialog);
  const { total } = useAppSelector(selectOrderValues);
  const { showReminder, hideReminder, isVisible } = useReminder();
  const dispatch = useAppDispatch();
  const isUnifiedMenu = getMenuUnificationFeatureFlag();

  // Recursively collect all cart item names, including nested ones in modifier groups
  const cartItemNames = useMemo(() => {
    if (!validCartItems) {
      return [];
    }
    return getAllCartItemNames(validCartItems);
  }, [validCartItems]);

  // Get the actual slash commands from tts-prompt category items
  const { hasRequiredCommands, availableSlashCommands } = useMemo(() => {
    const ttsPromptItems = Object.values(topLevelMenuItems).filter(
      (item) =>
        item.category.includes(TTS_PROMPT) ||
        item.category.includes(TTS_PROMPT_PREFIX)
    );

    // Filter items that have the required commands and they're active
    let relevantItems = ttsPromptItems.filter((item) => {
      if (!item.inactive && item.name) {
        const [intent] = item.name.split(':');
        // Exact match for donation and customer_info, prefix match for upsell_end_of_order
        return (
          [
            SLASH_COMMAND_REMINDERS.DONATION,
            SLASH_COMMAND_REMINDERS.CUSTOMER_INFO,
          ].includes(intent) ||
          intent.startsWith(SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX)
        );
      }
      return false;
    });

    // HITL-1570: a donation whose constraint declares speak_total_in_upsell
    // is only offered while a check total exists — without one the donation
    // upsell is skipped: left out of the reminder text and never seeded into
    // the queue. Items without the condition are untouched.
    relevantItems = relevantItems.filter((item) => {
      const [intent] = item.name.split(':');
      if (intent !== SLASH_COMMAND_REMINDERS.DONATION) return true;
      return !item.speakTotalInUpsell || Boolean(total);
    });

    // For upsell_end_of_order items, further filter by CONSTRAINT_ITEM_NOT_ON_CHECK
    if (
      isUnifiedMenu &&
      cartItemNames.length > 0 &&
      Object.keys(brandMenuConstraints).length > 0
    ) {
      relevantItems = relevantItems.filter((item) => {
        const [intent] = item.name.split(':');
        const isUpsell = intent.startsWith(
          SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
        );
        if (!isUpsell) return true;

        // Find the underlying brand menu item via the top-level item's id
        const bmItem = brandMenuItemsByName[item.id];
        const primaryConstraintName = bmItem?.constraints?.[0];
        const constraint: IConstraint | undefined = primaryConstraintName
          ? brandMenuConstraints[primaryConstraintName]
          : undefined;

        return evaluateItemNotOnCheckConstraint(
          constraint,
          brandMenuConstraints,
          cartItemNames
        );
      });
    }

    // Sort items to ensure upsell_end_of_order comes first, then donation, then customer_info
    const sortedItems = relevantItems.sort((a, b) => {
      const aIntent = a.name?.split(':')[0] || '';
      const bIntent = b.name?.split(':')[0] || '';

      const aIsDonation = aIntent === SLASH_COMMAND_REMINDERS.DONATION;
      const bIsDonation = bIntent === SLASH_COMMAND_REMINDERS.DONATION;
      const aIsCustomerInfo = aIntent === SLASH_COMMAND_REMINDERS.CUSTOMER_INFO;
      const bIsCustomerInfo = bIntent === SLASH_COMMAND_REMINDERS.CUSTOMER_INFO;
      const aIsUpsellEndOfOrder = aIntent.startsWith(
        SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
      );
      const bIsUpsellEndOfOrder = bIntent.startsWith(
        SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
      );

      // Upsell end of order items come first
      if (aIsUpsellEndOfOrder && !bIsUpsellEndOfOrder) return -1;
      if (!aIsUpsellEndOfOrder && bIsUpsellEndOfOrder) return 1;

      // Donation items come after upsell_end_of_order
      if (aIsDonation && !bIsDonation && !bIsUpsellEndOfOrder) return -1;
      if (!aIsDonation && bIsDonation && !aIsUpsellEndOfOrder) return 1;

      // Customer info items come after donation
      if (
        aIsCustomerInfo &&
        !bIsCustomerInfo &&
        !bIsDonation &&
        !bIsUpsellEndOfOrder
      )
        return -1;
      if (
        !aIsCustomerInfo &&
        bIsCustomerInfo &&
        !aIsDonation &&
        !aIsUpsellEndOfOrder
      )
        return 1;

      // Other items maintain their relative order
      return 0;
    });

    // Extract commands from sorted items
    const commands: SlashCommand[] = [];
    sortedItems.forEach((item) => {
      if (item.slashCommands && item.slashCommands.length > 0) {
        commands.push({ intent: item.name, command: item.slashCommands[0] });
      } else {
        // Handle PRP menu setup
        const [intent, command] = item.name.split(':');
        if (command) {
          commands.push({ intent, command });
        }
      }
    });

    return {
      hasRequiredCommands: relevantItems.length > 0,
      availableSlashCommands: commands,
    };
  }, [
    topLevelMenuItems,
    isUnifiedMenu,
    cartItemNames,
    brandMenuConstraints,
    brandMenuItemsByName,
    total,
  ]);

  const queue = dialogState.slashCommands ?? [];
  const queueIndex = dialogState.slashCommandIndex ?? 0;

  // HITL-1253: keep the displayed reminder in sync with the queue as slash
  // commands are consumed (e.g. from the OmniBar advancing the index). Without
  // this, the reminder would keep showing "ask for upsell, donation, customer"
  // even after the agent has triggered upsell.
  useEffect(() => {
    if (!isVisible) return;
    if (queue.length === 0) return;

    const pending = queue.slice(queueIndex);
    if (pending.length === 0) {
      hideReminder();
      return;
    }
    const message = buildReminderMessage(pending);
    if (message) {
      showReminder(message);
    } else {
      hideReminder();
    }
    // showReminder/hideReminder/buildReminderMessage are stable per render and
    // re-running on their identity churn would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, queueIndex, isVisible]);

  /**
   * Build the displayed reminder text from a list of slash commands.
   * Labels appear in the order their commands appear in the input list, with
   * duplicates collapsed. Returns an empty string if no recognized commands
   * are present.
   */
  const buildReminderMessage = (commands: SlashCommand[]): string => {
    const labels: string[] = [];
    commands.forEach((cmd) => {
      let label: string | null = null;
      switch (cmd.intent) {
        case SLASH_COMMAND_REMINDERS.DONATION:
          label = 'donation';
          break;
        case SLASH_COMMAND_REMINDERS.CUSTOMER_INFO:
          label = 'customer name';
          break;
        default:
          if (
            cmd.intent.startsWith(
              SLASH_COMMAND_REMINDERS.UPSELL_END_OF_ORDER_PREFIX
            )
          ) {
            label = 'end of order upsell';
          }
      }
      if (label && !labels.includes(label)) labels.push(label);
    });

    return labels.length === 0
      ? ''
      : `Remember to ask for ${labels.join(', ')}`;
  };

  /**
   * Shows the appropriate slash command reminder message.
   * Only shows reminder if required commands exist in tts-prompt category.
   * Pass `{ excludeUpsell: true }` from contexts (like readback) where the
   * end-of-order upsell shouldn't be added/re-added to the reminder.
   */
  const showSlashCommandReminder = (options?: ShowReminderOptions) => {
    const excludeUpsell = !!options?.excludeUpsell;
    const queueIsSeeded = queue.length > 0;
    const allConsumed = queueIsSeeded && queueIndex >= queue.length;

    // If every queued command has already fired, hide the reminder. Re-clicking
    // Confirm/Readback after the agent worked through everything shouldn't
    // re-show stale prompts.
    if (allConsumed) {
      hideReminder();
      return;
    }

    let commandsToShow: SlashCommand[];

    // HITL-1253: when excluding upsell against an already-seeded queue (the
    // readback path), derive the text from what's actually pending in the
    // queue rather than from `availableSlashCommands` — otherwise the
    // reminder can mention commands the agent has already triggered.
    if (excludeUpsell && queueIsSeeded) {
      // Advance redux state past any leading upsell, so the upsell prompt
      // won't fire next via slash command. For the displayed text we just
      // take the pending slice and drop any upsell — the dispatched skip
      // and the local filter converge on the same result.
      dispatch(skipUpsellInQueue());
      commandsToShow = queue
        .slice(queueIndex)
        .filter((c) => !isUpsellCommand(c));
    } else {
      // Confirm path, or readback with empty queue (fresh seed).
      if (!hasRequiredCommands) return;

      commandsToShow = excludeUpsell
        ? availableSlashCommands.filter((c) => !isUpsellCommand(c))
        : availableSlashCommands;
    }

    // Nothing to show → hide any stale reminder. `hideReminder` is idempotent
    // when the reminder wasn't visible to begin with.
    if (commandsToShow.length === 0) {
      hideReminder();
      return;
    }

    const message = buildReminderMessage(commandsToShow);
    // For the seeded-queue branch, ReminderContext's length-guard prevents
    // re-seeding — so this only updates the displayed text.
    if (message) showReminder(message, commandsToShow);
  };

  return {
    hasRequiredCommands,
    availableSlashCommands,
    showSlashCommandReminder,
  };
};

export default useSlashCommandReminder;
